import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { managerAPI } from '../config/api';
import APP_COLOURS from '../config/colors';
import { getAvatarImage } from '../config/avatars';
import Header from './Dashboard/Header';
import LiveTracker from './Dashboard/LiveTracker';
import Calendar from './Dashboard/Calendar';
import { StaffList, StaffProfile, StaffSalesView } from './Dashboard/index';
import NewStaffForm from './Dashboard/NewStaffForm';
import ManagerProfile from './Dashboard/ManagerProfile';
import DeviceManagement from './DeviceManagement';

const Dashboard = ({ userData, onLogout }) => {
  const [staff, setStaff] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [mallFilter, setMallFilter] = useState('all');
  const [retailerFilter, setRetailerFilter] = useState('all');
  const [showStaffSales, setShowStaffSales] = useState(false);
  const [selectedStaffForSales, setSelectedStaffForSales] = useState(null);
  const [showNewStaffForm, setShowNewStaffForm] = useState(false);
  const [showManagerProfile, setShowManagerProfile] = useState(false);
  const [showDeviceManagement, setShowDeviceManagement] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(userData);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [todaySnapshot, setTodaySnapshot] = useState(null);

  // Track screen width for responsive layout
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = screenWidth < 768;

  // Scroll to top on mount so tabs are always visible below the fixed header
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch staff data and devices on component mount
  useEffect(() => {
    fetchStaff();
    fetchDevices();
    fetchTodaySnapshot();
    
    // Set up heartbeat interval
    const heartbeatInterval = setInterval(() => {
      sendHeartbeat();
    }, 5 * 60 * 1000); // Every 5 minutes

    return () => clearInterval(heartbeatInterval);
  }, []);

  const fetchStaff = async () => {
    try {
      const staffData = await managerAPI.getStaff();
      setStaff(staffData);
      
      // If a staff member is currently selected, update their data with fresh info
      if (selectedStaff) {
        const updatedSelectedStaff = staffData.find(s => s.id === selectedStaff.id);
        if (updatedSelectedStaff) {
          setSelectedStaff(updatedSelectedStaff);
        }
      }
      
      setError('');
    } catch (err) {
      console.error('Error fetching staff:', err);
      if (err.message === 'Authentication failed') {
        handleLogout();
      } else {
        setError(err.message || 'Failed to fetch staff data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDevices = async () => {
    try {
      const devicesData = await managerAPI.getDevices();
      
      // Sanitize device data to extract strings from objects
      const sanitizedDevices = Array.isArray(devicesData) ? devicesData.map(device => ({
        ...device,
        mall: device.mall?.title || device.mall || '',
        retailer: device.retailer?.title || device.retailer || '',
      })) : [];
      
      setDevices(sanitizedDevices);
    } catch (err) {
      console.error('Error fetching devices:', err);
      // Don't show error for devices as it's not critical
    }
  };

  const fetchTodaySnapshot = async () => {
    try {
      const response = await managerAPI.getTodaySnapshot();
      setTodaySnapshot(response);
    } catch (err) {
      console.error('Error fetching today snapshot:', err);
    }
  };

  const sendHeartbeat = async () => {
    try {
      await managerAPI.heartbeat();
    } catch (err) {
      console.error('Heartbeat failed:', err);
    }
  };

  const handleLogout = () => {
    // Fire logout activity log before clearing the token (fire-and-forget)
    if (managerAPI.logout) {
      managerAPI.logout().catch(() => {});
    }
    localStorage.removeItem('traq_access_token');
    localStorage.removeItem('traq_user_data');
    onLogout();
  };

  const handleViewStaffSales = (staffMember) => {
    setSelectedStaffForSales(staffMember);
    setShowStaffSales(true);
  };

  const handleCloseStaffSales = () => {
    setShowStaffSales(false);
    setSelectedStaffForSales(null);
  };

  const handleNewStaffSuccess = () => {
    // Refresh staff list after successful creation
    fetchStaff();
  };

  const handleProfileUpdate = (updatedData) => {
    setCurrentUserData(updatedData);
  };

  const activeStaff = staff.filter(member => member.archived !== true);

  const uniqueMalls = [...new Set(
    activeStaff
      .map(member => member.mall?.title)
      .filter(Boolean)
  )].sort();

  const uniqueRetailers = [...new Set(
    activeStaff
      .map(member => member.retailer?.title)
      .filter(Boolean)
  )].sort();

  const allocatedStaff = activeStaff.filter(member => {
    const isAllocated = member.manager?.some(m => m.id === currentUserData.id);
    return isAllocated;
  });

  const filteredStaff = staff.filter(member => {
    if (member.archived === true) return false;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
      const email = member.email?.toLowerCase() || '';
      const mallTitle = member.mall?.title?.toLowerCase() || '';
      const retailerTitle = member.retailer?.title?.toLowerCase() || '';
      
      const matchesSearch = fullName.includes(query) || 
           email.includes(query) || 
           mallTitle.includes(query) ||
           retailerTitle.includes(query);
      
      if (!matchesSearch) return false;
    }
    
    // Mall filter
    if (mallFilter !== 'all' && member.mall?.title !== mallFilter) {
      return false;
    }
    
    // Retailer filter
    if (retailerFilter !== 'all' && member.retailer?.title !== retailerFilter) {
      return false;
    }
    
    return true;
  });

  // Build a lookup map: staffId -> device they're on
  const staffDeviceMap = useMemo(() => {
    const map = {};
    devices.forEach(device => {
      const users = Array.isArray(device.users) ? device.users : [];
      users.forEach(u => {
        const uid = typeof u === 'string' ? u : (u?.id || u?.uid || '');
        if (uid) {
          map[uid] = {
            id: device.id,
            mall: device.mall?.title || device.mall || '',
            retailer: device.retailer?.title || device.retailer || '',
          };
        }
      });
    });
    return map;
  }, [devices]);

  // Build a lookup map: staffId -> today's attendance/sales record
  const staffTodayMap = useMemo(() => {
    if (!todaySnapshot?.records) return {};
    return todaySnapshot.records.reduce((acc, record) => {
      const id = record.staff_id || record.user_id;
      if (id) acc[id] = record;
      return acc;
    }, {});
  }, [todaySnapshot]);

  const formatSnapshotTime = (timeData) => {
    if (!timeData) return null;
    if (timeData._seconds) return moment.unix(timeData._seconds).format('HH:mm');
    if (typeof timeData === 'string') return moment(timeData).format('HH:mm');
    return null;
  };

  const formatSnapshotCurrency = (amount) => {
    const val = parseFloat(amount || 0);
    if (val === 0) return null;
    return 'R ' + val.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: APP_COLOURS.BG }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: APP_COLOURS.BLUE }}></div>
          <p style={{ color: APP_COLOURS.TEXTCOLOR }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // If viewing a staff profile, show it full screen
  if (selectedStaff) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden" style={{ backgroundColor: APP_COLOURS.BG }}>
        <Header 
          userData={currentUserData} 
          onLogout={handleLogout}
          onProfileClick={() => setShowManagerProfile(true)}
        />
        <div 
          className="max-w-[1920px] mx-auto overflow-y-auto"
          style={{ 
            paddingTop: isMobile ? '70px' : '96px',
            paddingLeft: isMobile ? '6px' : '24px',
            paddingRight: isMobile ? '6px' : '24px',
            paddingBottom: isMobile ? '6px' : '24px',
          }}
        >
          <StaffProfile 
            selectedStaff={selectedStaff}
            setSelectedStaff={setSelectedStaff}
            managerId={currentUserData.id}
            onAllocationChange={fetchStaff}
          />
        </div>
        
        {/* Manager Profile Modal */}
        {showManagerProfile && (
          <ManagerProfile
            userData={currentUserData}
            onClose={() => setShowManagerProfile(false)}
            onUpdateSuccess={handleProfileUpdate}
          />
        )}
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'My Staff', icon: 'fa-user-friends' },
    { id: 'allstaff', label: 'All Staff', icon: 'fa-users' },
    { id: 'leaderboard', label: 'Leaderboard', icon: 'fa-trophy' },
  ];

  return (
    <div className="min-h-screen w-full overflow-x-hidden" style={{ backgroundColor: APP_COLOURS.BG }}>
      {/* Fixed Header */}
      <Header 
        userData={currentUserData} 
        onLogout={handleLogout}
        onProfileClick={() => setShowManagerProfile(true)}
      />

      {/* Main Content - starts after header */}
      <div 
        className="max-w-[1920px] mx-auto"
        style={{ 
          paddingTop: isMobile ? '80px' : '100px',
          paddingLeft: isMobile ? '10px' : '24px',
          paddingRight: isMobile ? '10px' : '24px',
          paddingBottom: isMobile ? '16px' : '24px',
        }}
      >
        {/* Error Message */}
        {error && (
          <div 
            className="rounded-xl" 
            style={{ 
              backgroundColor: 'rgba(255, 95, 95, 0.1)', 
              border: `1px solid ${APP_COLOURS.ERROR}`,
              marginBottom: isMobile ? '12px' : '16px',
              padding: isMobile ? '10px' : '16px',
            }}
          >
            <div className="flex items-center">
              <i className={`fas fa-exclamation-circle ${isMobile ? 'mr-2 text-sm' : 'mr-3'}`} style={{ color: APP_COLOURS.ERROR }}></i>
              <p className={isMobile ? 'text-xs' : 'text-sm'} style={{ color: APP_COLOURS.ERROR }}>{error}</p>
            </div>
          </div>
        )}

        {/* Tab Bar - sticky below fixed header */}
        <div
          className="mx-auto"
          style={{
            maxWidth: '1600px',
            position: 'sticky',
            top: isMobile ? '60px' : '88px',
            zIndex: 40,
            paddingTop: isMobile ? '6px' : '8px',
            paddingBottom: isMobile ? '10px' : '14px',
            backgroundColor: APP_COLOURS.BG,
            marginBottom: isMobile ? '4px' : '8px',
          }}
        >
          <div
            className="inline-flex rounded-xl p-1"
            style={{
              backgroundColor: APP_COLOURS.BG3,
              border: `1px solid ${APP_COLOURS.BG4}`,
            }}
          >
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center rounded-lg font-semibold transition-all duration-200 ${isMobile ? 'px-3 py-1.5 text-xs gap-1.5' : 'px-5 py-2 text-sm gap-2'}`}
                  style={{
                    backgroundColor: isActive ? APP_COLOURS.BLUE : 'transparent',
                    color: isActive ? '#fff' : APP_COLOURS.PLACEHOLDER,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <i className={`fas ${tab.icon}`} style={{ fontSize: isMobile ? '11px' : '13px' }}></i>
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'leaderboard' && (
          <div className="mx-auto" style={{ maxWidth: '1600px' }}>
            <LiveTracker staff={staff} onStaffClick={setSelectedStaff} />
          </div>
        )}

        {activeTab === 'allstaff' && (
          <div className="mx-auto" style={{ maxWidth: '1600px' }}>
            <StaffList
              staff={staff}
              filteredStaff={filteredStaff}
              selectedStaff={selectedStaff}
              setSelectedStaff={setSelectedStaff}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              mallFilter={mallFilter}
              setMallFilter={setMallFilter}
              uniqueMalls={uniqueMalls}
              retailerFilter={retailerFilter}
              setRetailerFilter={setRetailerFilter}
              uniqueRetailers={uniqueRetailers}
            />
          </div>
        )}

        {activeTab === 'dashboard' && (
          <div className="mx-auto" style={{ maxWidth: isMobile ? '100%' : '680px' }}>
            {/* Header */}
            <div style={{ marginBottom: isMobile ? '14px' : '20px' }}>
              <p className={isMobile ? 'text-xs' : 'text-sm'} style={{ color: APP_COLOURS.PLACEHOLDER }}>
                {allocatedStaff.length} staff member{allocatedStaff.length !== 1 ? 's' : ''} allocated to you
              </p>
            </div>

            {allocatedStaff.length === 0 ? (
              <div
                className="rounded-2xl text-center"
                style={{
                  backgroundColor: APP_COLOURS.BG3,
                  border: `1px solid ${APP_COLOURS.BG4}`,
                  padding: isMobile ? '32px 16px' : '48px 24px',
                }}
              >
                <i className="fas fa-user-slash" style={{ fontSize: isMobile ? '36px' : '48px', color: APP_COLOURS.PLACEHOLDER, opacity: 0.25, marginBottom: '12px', display: 'block' }}></i>
                <p className={isMobile ? 'text-sm' : 'text-base'} style={{ color: APP_COLOURS.PLACEHOLDER }}>
                  No staff members allocated yet
                </p>
                <p className={`${isMobile ? 'text-xs mt-1' : 'text-sm mt-2'}`} style={{ color: APP_COLOURS.PLACEHOLDER, opacity: 0.6 }}>
                  Go to the All Staff tab to allocate staff to you
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '10px' : '12px' }}>
                {allocatedStaff.map((member) => {
                  const todayRecord = staffTodayMap[member.id];
                  const clockIn = todayRecord ? formatSnapshotTime(todayRecord.checkin || todayRecord.clockIn) : null;
                  const clockOut = todayRecord ? formatSnapshotTime(todayRecord.checkout || todayRecord.clockOut) : null;
                  const lunchStart = todayRecord ? formatSnapshotTime(todayRecord.lunch_start) : null;
                  const lunchEnd = todayRecord ? formatSnapshotTime(todayRecord.lunch_end) : null;
                  const onLunch = lunchStart && !lunchEnd;
                  // trs may be on the record directly, or derivable from categoryPerformance
                  const rawTrs = todayRecord?.trs ?? todayRecord?.totalSales
                    ?? (Array.isArray(todayRecord?.categoryPerformance) && todayRecord.categoryPerformance.length > 0
                      ? todayRecord.categoryPerformance.reduce((sum, cat) => sum + (parseFloat(cat.total) || 0), 0)
                      : undefined);
                  const totalSales = formatSnapshotCurrency(rawTrs);
                  const isCheckedIn = !!clockIn;
                  const avatarId = member.avatar?.toString() || '1';
                  const memberDevice = staffDeviceMap[member.id];

                  return (
                    <div
                      key={member.id}
                      onClick={() => setSelectedStaff(member)}
                      className="rounded-xl cursor-pointer transition-all duration-200"
                      style={{
                        backgroundColor: APP_COLOURS.BG3,
                        border: `1px solid ${APP_COLOURS.BG4}`,
                        padding: isMobile ? '12px' : '14px 16px',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = APP_COLOURS.BLUE;
                        e.currentTarget.style.backgroundColor = APP_COLOURS.BG4;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = APP_COLOURS.BG4;
                        e.currentTarget.style.backgroundColor = APP_COLOURS.BG3;
                      }}
                    >
                      <div className="flex items-center">
                        {/* Avatar */}
                        <div className="relative flex-shrink-0">
                          <img
                            src={getAvatarImage(avatarId)}
                            alt={`${member.first_name} ${member.last_name}`}
                            className="rounded-full object-cover"
                            style={{
                              width: isMobile ? '42px' : '46px',
                              height: isMobile ? '42px' : '46px',
                              border: `2px solid ${isCheckedIn ? APP_COLOURS.BLUE : APP_COLOURS.BG5}`,
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div
                            className="rounded-full items-center justify-center text-white font-bold absolute top-0 left-0"
                            style={{
                              width: isMobile ? '42px' : '46px',
                              height: isMobile ? '42px' : '46px',
                              background: `linear-gradient(135deg, ${APP_COLOURS.BLUE}, ${APP_COLOURS.PURPLE})`,
                              border: `2px solid ${isCheckedIn ? APP_COLOURS.BLUE : APP_COLOURS.BG5}`,
                              display: 'none',
                              fontSize: '13px',
                            }}
                          >
                            {(member.first_name?.[0] || '') + (member.last_name?.[0] || '')}
                          </div>
                          <div
                            className="absolute rounded-full"
                            style={{
                              bottom: '1px',
                              right: '1px',
                              width: '11px',
                              height: '11px',
                              backgroundColor: isCheckedIn
                                ? (onLunch ? APP_COLOURS.ORANGE : (clockOut ? APP_COLOURS.PLACEHOLDER : APP_COLOURS.SUCCESS))
                                : APP_COLOURS.BG5,
                              border: `2px solid ${APP_COLOURS.BG3}`,
                            }}
                          />
                        </div>

                        {/* Name + Today Info */}
                        <div className="flex-1 min-w-0" style={{ marginLeft: isMobile ? '10px' : '12px' }}>
                          <div className="flex items-center justify-between">
                            <p className={`font-semibold truncate ${isMobile ? 'text-[13px]' : 'text-sm'}`} style={{ color: APP_COLOURS.TEXTCOLOR }}>
                              {member.first_name} {member.last_name}
                            </p>
                            {/* Sales on the right */}
                            <div className="flex items-center gap-1 flex-shrink-0" style={{ marginLeft: '8px' }}>
                              <span style={{ fontSize: isMobile ? '12px' : '13px', fontWeight: 700, color: totalSales ? APP_COLOURS.GREEN : APP_COLOURS.PLACEHOLDER, opacity: totalSales ? 1 : 0.25 }}>
                                {totalSales || 'R 0.00'}
                              </span>
                            </div>
                          </div>
                          <p className="truncate" style={{ fontSize: isMobile ? '10px' : '11px', color: APP_COLOURS.PLACEHOLDER, marginTop: '2px' }}>
                            {member.position || 'No position'}
                            {member.mall?.title ? ` · ${member.mall.title}` : ''}
                            {member.retailer?.title ? ` · ${member.retailer.title}` : ''}
                            {memberDevice && (
                              <span style={{ color: APP_COLOURS.BLUE, opacity: 0.7 }}>
                                {' · '}<i className="fas fa-tablet-alt" style={{ fontSize: '8px', marginRight: '2px' }}></i>
                                {memberDevice.retailer || memberDevice.mall || memberDevice.id}
                              </span>
                            )}
                          </p>
                          {/* Today attendance badges */}
                          <div className="flex items-center gap-1.5 flex-wrap" style={{ marginTop: '6px' }}>
                            <span
                              className="inline-flex items-center gap-1 rounded"
                              style={{
                                padding: '1px 5px',
                                fontSize: '10px',
                                fontWeight: 600,
                                backgroundColor: isCheckedIn ? `${APP_COLOURS.SUCCESS}15` : `${APP_COLOURS.BG5}50`,
                                color: isCheckedIn ? APP_COLOURS.SUCCESS : APP_COLOURS.PLACEHOLDER,
                                opacity: isCheckedIn ? 1 : 0.5,
                              }}
                            >
                              <i className="fas fa-sign-in-alt" style={{ fontSize: '8px' }}></i>
                              {clockIn || 'Not in'}
                            </span>

                            {clockOut && (
                              <span
                                className="inline-flex items-center gap-1 rounded"
                                style={{ padding: '1px 5px', fontSize: '10px', backgroundColor: `${APP_COLOURS.BG5}50`, color: APP_COLOURS.PLACEHOLDER }}
                              >
                                <i className="fas fa-sign-out-alt" style={{ fontSize: '8px' }}></i>
                                {clockOut}
                              </span>
                            )}

                            {lunchStart && (
                              <span
                                className="inline-flex items-center gap-1 rounded"
                                style={{
                                  padding: '1px 5px',
                                  fontSize: '10px',
                                  fontWeight: onLunch ? 600 : 400,
                                  backgroundColor: onLunch ? `${APP_COLOURS.ORANGE}18` : `${APP_COLOURS.BG5}50`,
                                  color: onLunch ? APP_COLOURS.ORANGE : APP_COLOURS.PLACEHOLDER,
                                }}
                              >
                                <i className="fas fa-utensils" style={{ fontSize: '7px' }}></i>
                                {onLunch ? 'Lunch' : `${lunchStart}–${lunchEnd}`}
                              </span>
                            )}
                          </div>
                        </div>

                        <i className="fas fa-chevron-right flex-shrink-0" style={{ color: APP_COLOURS.PLACEHOLDER, fontSize: '10px', opacity: 0.3, marginLeft: '8px' }}></i>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* View Staff Devices Button */}
            {allocatedStaff.length > 0 && (
              <div style={{ marginTop: isMobile ? '16px' : '20px' }}>
                <button
                  onClick={() => setShowDeviceManagement(true)}
                  className={`rounded-xl font-semibold transition-all duration-200 flex items-center justify-center ${isMobile ? 'px-4 py-2.5 text-xs' : 'px-5 py-3 text-sm'}`}
                  style={{
                    backgroundColor: `${APP_COLOURS.BLUE}15`,
                    color: APP_COLOURS.BLUE,
                    border: `1px solid ${APP_COLOURS.BLUE}30`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${APP_COLOURS.BLUE}25`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = `${APP_COLOURS.BLUE}15`;
                  }}
                >
                  <i className={`fas fa-tablet-alt ${isMobile ? 'mr-1.5' : 'mr-2'}`}></i>
                  View Staff Devices
                  {devices.length > 0 && (
                    <span
                      className="rounded-full font-bold ml-2"
                      style={{ padding: '2px 8px', fontSize: '10px', backgroundColor: APP_COLOURS.BLUE, color: '#fff' }}
                    >
                      {devices.length}
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Staff Sales Modal */}
      {showStaffSales && (
        <StaffSalesView
          staffMember={selectedStaffForSales}
          onClose={handleCloseStaffSales}
        />
      )}

      {/* New Staff Form Modal */}
      {showNewStaffForm && (
        <NewStaffForm
          onClose={() => setShowNewStaffForm(false)}
          onSuccess={handleNewStaffSuccess}
        />
      )}

      {/* Manager Profile Modal */}
      {showManagerProfile && (
        <ManagerProfile
          userData={currentUserData}
          onClose={() => setShowManagerProfile(false)}
          onUpdateSuccess={handleProfileUpdate}
        />
      )}

      {/* Device Management Modal */}
      {showDeviceManagement && (
        <DeviceManagement
          onClose={() => setShowDeviceManagement(false)}
          staffList={staff}
        />
      )}
    </div>
  );
};

export default Dashboard;
